import {
  Button,
  CondensedButton,
  Flex,
  Heading,
  IconModuleLine,
  IconSaveLine,
  IconTrashLine,
  Spinner,
  Text,
  Tooltip,
  View,
} from "@instructure/ui";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { ExternalService } from "../../../../../../services/ExternalService";
import { fileWriter } from "../../../../../../utils/FileUtils";
import { Card } from "../../../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../../../CustomHooks/NotificationProvider";
import RichTextEditor, {
  rteHasContent,
} from "../../../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../../../RichTextArea/RichTextViewer";
import { MeasureVersionsSelect } from "../../../../../Selectors/MeasureVersionsSelect";
import QualitativeMeasureEvidenceModal from "./QualitativeMeasureEvidenceModal";

const empty_response = {
  id: "",
  content: "",
  is_draft: false,
  is_latest: false,
  author: "",
};

const NonStructuredQuestions = ({
  measure,
  instructions,
  userEmail,
  isPreview,
  permission,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [loading, setLoading] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState(empty_response);
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [versions, setVersions] = useState([]);

  const isDraft = selectedResponse.is_draft ?? false;

  const fetchVersionHistory = useCallback(async () => {
    try {
      const versions = await ExternalService.getContentVersionHistoryExternal(
        "qualitativeEvidence",
        measure.ddl_id,
        userEmail
      );

      // Extract draft entry (if present) and load into editor
      const draftEntry = versions.find((v) => v.is_draft);
      const published = versions.filter((v) => !v.is_draft);

      if (draftEntry && !isPreview) {
        setSelectedResponse(draftEntry);
      } else if (published.length > 0) {
        // Load latest published version into editor
        const latest = published.find((v) => v.is_latest) || published[0];
        setSelectedResponse(latest);
      } else {
        setSelectedResponse(empty_response);
      }

      setVersions(versions);
    } catch {
      setSelectedResponse(empty_response);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [isPreview, measure.ddl_id, userEmail]);

  useEffect(() => {
    fetchVersionHistory();
  }, [fetchVersionHistory]);

  function handleVersionSelect(versionId) {
    if (versionId === selectedResponse.id) return;
    const selected = versions.find((v) => v.id === versionId);
    if (selected) {
      setSelectedResponse(selected);
    }
  }

  async function handleDeleteDraft() {
    setDeletingDraft(true);
    try {
      const path = `${measure.ddl_id}/draft`;
      const metaData = { author: userEmail };
      await ExternalService.deleteFile(
        "response.html",
        "qualitativeEvidence",
        path,
        metaData
      );

      const newVersions = versions.filter((v) => !v.is_draft);

      // Revert editor to the latest published version or empty state
      const latestPublished =
        newVersions.find((v) => v.is_latest) || newVersions[0];
      if (latestPublished) {
        setSelectedResponse(latestPublished);
      } else {
        setSelectedResponse(empty_response);
      }
      setVersions(newVersions);
      showNotification(
        t("notifications.general.deleteSuccess", { obj: t("global.draft") })
      );
    } catch {
      showNotification(
        t("notifications.general.deleteError", { obj: t("global.draft") }),
        ERROR
      );
    } finally {
      setDeletingDraft(false);
    }
  }

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const measureDraft = fileWriter(
        selectedResponse.content,
        "response.html",
        "text/html"
      );
      const metaData = { author: userEmail };

      await ExternalService.saveFile(
        measureDraft,
        "qualitativeEvidence",
        `${measure.ddl_id}/draft`,
        metaData
      );

      await fetchVersionHistory();
      showNotification(
        t("notifications.general.saveSuccess", { obj: t("global.draft") })
      );
    } catch {
      showNotification(
        t("notifications.general.saveError", { obj: t("global.draft") }),
        ERROR
      );
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const measureResponse = fileWriter(
        selectedResponse.content,
        "response.html",
        "text/html"
      );
      const metaData = { author: userEmail };

      await ExternalService.saveFile(
        measureResponse,
        "qualitativeEvidence",
        measure.ddl_id,
        metaData
      );

      // Delete the draft after successful publish
      const hasDraft = versions.some((v) => v.is_draft);
      if (hasDraft) {
        try {
          const path = `${measure.ddl_id}/draft`;
          const metaData = { author: userEmail };
          await ExternalService.deleteFile(
            "response.html",
            "qualitativeEvidence",
            path,
            metaData
          );
        } catch (deleteError) {
          console.warn("Failed to delete draft after publish:", deleteError);
        }
      }

      await fetchVersionHistory();
      showNotification(
        t("notifications.assessmentResults.publishMeasureSuccess")
      );
    } catch {
      showNotification(
        t("notifications.assessmentResults.publishMeasureError"),
        ERROR
      );
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Flex wrap="wrap" gap="small" alignItems="center">
          <Flex.Item>
            <Heading level="h3">{measure.name}</Heading>
          </Flex.Item>
          {!isPreview && (
            <Flex.Item shouldGrow>
              <Text fontStyle="italic">
                {t("measures.assignedTo")}: {measure.assignees}
              </Text>
            </Flex.Item>
          )}
          {!isPreview && versions.length > 0 && (
            <Flex.Item width="600px">
              <MeasureVersionsSelect
                versions={versions}
                selectedVersionId={selectedResponse.id}
                onVersionSelect={handleVersionSelect}
              />
            </Flex.Item>
          )}
        </Flex>
      </Flex.Item>
      {rteHasContent(instructions) && (
        <Flex.Item size="100%" padding="0 0 0 large">
          <View
            as="div"
            borderWidth="none none small none"
            padding="0 0 x-small 0"
          >
            <RichTextViewer
              value={instructions}
              ariaLabel={t("assessment.instructions")}
            />
          </View>
        </Flex.Item>
      )}
      {loading ? (
        <Flex.Item shouldGrow>
          <Skeleton height="10rem" count={2} style={{ marginBottom: 10 }} />
        </Flex.Item>
      ) : (
        <Flex.Item size="100%" padding="0 0 0 large">
          {permission ? (
            <Card
              style={{
                backgroundColor: "#F2F8FA",
                border: "1px solid #ccc",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <RichTextEditor
                  key={`${measure.ddl_id}-${selectedResponse.id || 0}`}
                  id={measure.ddl_id}
                  label={
                    <Text weight="bold">{t("results.measureResults")}</Text>
                  }
                  defaultValue={selectedResponse.content}
                  onChange={(value) =>
                    setSelectedResponse((prev) => ({ ...prev, content: value }))
                  }
                />
              </div>
              <Flex gap="small" wrap="wrap">
                {!isPreview && measure.upload_evidence && (
                  <Flex.Item textAlign="start">
                    <ManageEvidenceButton
                      measure={measure}
                      useEmail={userEmail}
                      isAssigned={permission}
                    />
                  </Flex.Item>
                )}
                {isDraft && (
                  <Flex.Item shouldGrow textAlign="end">
                    <Button
                      color="danger"
                      size="small"
                      onClick={handleDeleteDraft}
                      disabled={deletingDraft || savingDraft || publishing}
                      renderIcon={
                        deletingDraft ? (
                          <Spinner
                            renderTitle={t("global.pleaseWait")}
                            size="x-small"
                          />
                        ) : (
                          IconTrashLine
                        )
                      }
                    >
                      {t("global.deleteDraft")}
                    </Button>
                  </Flex.Item>
                )}
                <Flex.Item shouldGrow={!isDraft} textAlign="end">
                  <Button
                    color="secondary"
                    size="small"
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    renderIcon={
                      savingDraft ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : (
                        IconSaveLine
                      )
                    }
                  >
                    {isDraft ? t("global.updateDraft") : t("global.saveDraft")}
                  </Button>
                </Flex.Item>
                <Flex.Item>
                  <Tooltip
                    renderTip={t("results.publishRequirementMessage")}
                    on={
                      !rteHasContent(selectedResponse.content)
                        ? ["hover", "focus"]
                        : []
                    }
                  >
                    <Button
                      color="primary"
                      size="small"
                      onClick={handlePublish}
                      disabled={
                        publishing || !rteHasContent(selectedResponse.content)
                      }
                      renderIcon={
                        publishing ? (
                          <Spinner
                            renderTitle={t("global.pleaseWait")}
                            size="x-small"
                          />
                        ) : (
                          IconSaveLine
                        )
                      }
                    >
                      {t("results.publishMeasureResults")}
                    </Button>
                  </Tooltip>
                </Flex.Item>
              </Flex>
            </Card>
          ) : (
            <Card style={{ border: "1px solid #ccc" }}>
              {rteHasContent(selectedResponse.content) ? (
                <RichTextViewer
                  id={measure.ddl_id}
                  ariaLabel={t("results.measureResults")}
                  value={selectedResponse.content}
                />
              ) : (
                <Text fontStyle="italic">
                  {t("results.noResponsePublished")}
                </Text>
              )}
              {!isPreview && measure.upload_evidence && (
                <View
                  as="div"
                  borderWidth="small 0 0 0"
                  margin="small 0 0 0"
                  padding="small 0 0 0"
                >
                  <ManageEvidenceButton
                    measure={measure}
                    useEmail={userEmail}
                    isAssigned={permission}
                  />
                </View>
              )}
            </Card>
          )}
        </Flex.Item>
      )}
    </Flex>
  );
};

export default NonStructuredQuestions;

const ManageEvidenceButton = ({ measure, userEmail, isAssigned }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isPublished, setIsPublished] = useState();
  const [evidenceFiles, setEvidenceFiles] = useState(null);

  useEffect(() => {
    async function fetchMeasureEvidenceFiles() {
      try {
        const paths = await ExternalService.reviewFiles(
          "qualitativeEvidence",
          measure.ddl_id
        );
        const published = paths.some(
          (file) => file.file_name === "response.html"
        );
        setIsPublished(published);
        setEvidenceFiles(
          paths.filter(
            (file) =>
              file.file_name !== "response.html" &&
              !file.file_path.includes("/draft/")
          )
        );
      } catch {
        setEvidenceFiles([]);
      }
    }
    fetchMeasureEvidenceFiles();
  }, [measure]);

  const buttonLabel =
    evidenceFiles?.length > 0
      ? `${t("evidence.manageEvidence")} (${evidenceFiles.length})`
      : t("evidence.manageEvidence");

  return (
    <>
      <CondensedButton
        renderIcon={IconModuleLine}
        onClick={() => setOpen(true)}
      >
        {buttonLabel}
      </CondensedButton>
      <QualitativeMeasureEvidenceModal
        open={open}
        setOpen={setOpen}
        measureId={measure.ddl_id}
        measureName={measure.name}
        measureAssignees={measure.assignees}
        userEmail={userEmail}
        isPublished={isPublished}
        isAssigned={isAssigned}
        evidenceFiles={evidenceFiles}
        setEvidenceFiles={setEvidenceFiles}
      />
    </>
  );
};
